import React, { useState, useEffect } from "react";
import "./PongGame3D.css";
import * as THREE from "three";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import api from "@/services/api";

function PongGame3D() {
    const [canvas, setCanvas] = useState();

    // -----------------------------
    // SEND DATA TO BACKEND
    // -----------------------------
    const updateData = async (stats) => {

      try {

        await api.post("/match/3d", stats);

            console.log("Data send to server");
        } catch (error) {
            console.log("Data send to server failed", error);
        }
            console.log("Stats:", {
                playTime: stats.totalPlaySeconds,
                levelReached: stats.levelReached,
                levelsCompleted: stats.levelsCompleted,
                coins: stats.coins,
                maxBallSpeed: stats.maxBallSpeed,
                deaths: stats.deaths,
                lastBallSpeed: stats.lastBallSpeed
            });
    };

    useEffect(() => {
        if (!canvas) {
            return;
        };

        // -----------------------------
        // CONFIG
        // -----------------------------
        const STREAK_TO_SPAWN_COIN = 1;
        const MAX_LEVEL = 10;

        // Gradual difficulty ramp
        const LEVEL_SPEED_STEP = 0.02; // 2% per level-ish feel
        const CPU_MULT_MIN = 1.0;
        const CPU_MULT_MAX = 1.50;

        const LIVES_PER_LEVEL = 3;

        // Coin behavior
        const COIN_RELOCATE_SECONDS = 60.0;
        const COIN_Z_MIN = -92;   // farther away
        const COIN_Z_MAX = -10;   // closer to player (still in front of table)
        const COIN_X_PAD = 1.0;   // keep coin off the wall edges

        // -----------------------------
        // Scene / Renderer / Camera
        // -----------------------------
        const scene = new THREE.Scene();
        scene.fog = new THREE.Fog(0x07080b, 22, 190);

        const renderer = new THREE.WebGLRenderer({ antialias: true, canvas });
        renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
        renderer.setSize(window.innerWidth, window.innerHeight);
        renderer.outputColorSpace = THREE.SRGBColorSpace;

        renderer.physicallyCorrectLights = true;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.15;

        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;

        const camera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.1, 500);
        camera.position.set(0, 1.6, 18);
        camera.lookAt(0, 1.0, 0);

        // -----------------------------
        // Lighting
        // -----------------------------
        scene.add(new THREE.HemisphereLight(0xbfd3ff, 0x101018, 0.65));

        const dir = new THREE.DirectionalLight(0xffffff, 0.9);
        dir.position.set(10, 18, 12);
        dir.castShadow = true;
        dir.shadow.mapSize.set(2048, 2048);
        dir.shadow.bias = -0.00025;
        dir.shadow.normalBias = 0.02;
        dir.shadow.camera.near = 0.5;
        dir.shadow.camera.far = 80;
        dir.shadow.camera.left = -18;
        dir.shadow.camera.right = 18;
        dir.shadow.camera.top = 18;
        dir.shadow.camera.bottom = -18;
        scene.add(dir);

        // -----------------------------
        // Table
        // -----------------------------
        const tableTop = new THREE.Mesh(
            new THREE.BoxGeometry(22, 1.0, 130),
            new THREE.MeshStandardMaterial({ color: 0x2f8f2f, roughness: 0.9, metalness: 0.0 })
        );
        tableTop.position.set(0, -6.1, -42);
        tableTop.receiveShadow = true;
        scene.add(tableTop);

        const lineMat = new THREE.MeshStandardMaterial({ color: 0xe9edf7, roughness: 0.7, metalness: 0.0 });

        const longLineGeo = new THREE.BoxGeometry(0.18, 0.06, 128);
        const sideL = new THREE.Mesh(longLineGeo, lineMat);
        const sideR = new THREE.Mesh(longLineGeo, lineMat);
        sideL.position.set(-10.9, -5.58, -42);
        sideR.position.set(10.9, -5.58, -42);
        sideL.receiveShadow = sideR.receiveShadow = true;
        scene.add(sideL, sideR);

        const endLineGeo = new THREE.BoxGeometry(21.7, 0.06, 0.18);
        const endNear = new THREE.Mesh(endLineGeo, lineMat);
        const endFar = new THREE.Mesh(endLineGeo, lineMat);
        endNear.position.set(0, -5.58, 22);
        endFar.position.set(0, -5.58, -106);
        endNear.receiveShadow = endFar.receiveShadow = true;
        scene.add(endNear, endFar);

        const dashMat = new THREE.MeshStandardMaterial({ color: 0xf3f5fb, roughness: 0.75, metalness: 0.0 });
        const dashGeo = new THREE.BoxGeometry(0.22, 0.06, 2.4);
        const centerLine = new THREE.Group();
        for (let z = 22 - 3.0; z >= -106 + 3.0; z -= 6.0) {
            const dash = new THREE.Mesh(dashGeo, dashMat);
            dash.position.set(0, -5.58, z);
            dash.receiveShadow = true;
            centerLine.add(dash);
        }
        scene.add(centerLine);

        const ground = new THREE.Mesh(
            new THREE.PlaneGeometry(120, 340),
            new THREE.MeshStandardMaterial({ color: 0x0b0e18, roughness: 1.0, metalness: 0.0 })
        );
        ground.rotation.x = -Math.PI / 2;
        ground.position.y = -6.6;
        ground.position.z = -70;
        ground.receiveShadow = true;
        scene.add(ground);

        // -----------------------------
        // Paddle (player)
        // -----------------------------
        function roundedRectShape(w, h, r) {
            const s = new THREE.Shape();
            const x = -w / 2, y = -h / 2;
            s.moveTo(x + r, y);
            s.lineTo(x + w - r, y);
            s.quadraticCurveTo(x + w, y, x + w, y + r);
            s.lineTo(x + w, y + h - r);
            s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
            s.lineTo(x + r, y + h);
            s.quadraticCurveTo(x, y + h, x, y + h - r);
            s.lineTo(x, y + r);
            s.quadraticCurveTo(x, y, x + r, y);
            return s;
        }

        const paddle = new THREE.Group();
        scene.add(paddle);

        const bladeGeo = new THREE.ExtrudeGeometry(roundedRectShape(4.4, 5.6, 1.4), {
            depth: 0.35, bevelEnabled: true, bevelThickness: 0.18, bevelSize: 0.16, bevelSegments: 3
        });
        bladeGeo.center();

        const blade = new THREE.Mesh(
            bladeGeo,
            new THREE.MeshStandardMaterial({ color: 0xb6452c, roughness: 0.55, metalness: 0.05 })
        );
        blade.rotation.x = -0.12;
        blade.position.set(0, 1.2, 0);
        blade.castShadow = true;
        blade.receiveShadow = true;
        paddle.add(blade);

        const handleMat = new THREE.MeshStandardMaterial({ color: 0x3a2a1f, roughness: 0.75, metalness: 0.02 });
        const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.55, 0.72, 4.0, 18), handleMat);
        handle.position.set(0, -2.0, 0.1);
        handle.rotation.z = 0.08;
        handle.castShadow = true;
        handle.receiveShadow = true;
        paddle.add(handle);

        const handleCap = new THREE.Mesh(new THREE.SphereGeometry(0.62, 18, 18), handleMat);
        handleCap.position.set(0.05, -4.05, 0.1);
        handleCap.castShadow = true;
        handleCap.receiveShadow = true;
        paddle.add(handleCap);

        const paddleBaseY = -4.9;
        const paddleZ = 10.2;
        paddle.position.set(0, paddleBaseY, paddleZ);

        // -----------------------------
        // Opponent paddle
        // -----------------------------
        const lane = { xMin: -10.3, xMax: 10.3, zFarWall: -106, zServe: -95 };
        const opponentZ = lane.zFarWall + 6.2;

        const opponent = paddle.clone(true);
        scene.add(opponent);
        opponent.position.set(0, paddleBaseY, opponentZ);
        opponent.rotation.y = Math.PI;

        opponent.traverse((o) => {
            if (o.isMesh && o.material) {
                o.material = o.material.clone();
                if (o.material.color) o.material.color.offsetHSL(-0.02, 0.05, -0.05);
            }
        });

        // -----------------------------
        // Ball
        // -----------------------------
        const ballRadius = 0.65;
        const ball = new THREE.Mesh(
            new THREE.SphereGeometry(ballRadius, 28, 28),
            new THREE.MeshStandardMaterial({
                color: 0xff8c00,
                roughness: 0.35,
                metalness: 0.08,
                emissive: 0xff6a00,
                emissiveIntensity: 0.35
            })
        );
        ball.castShadow = true;
        scene.add(ball);

        const ballLight = new THREE.PointLight(0xffa000, 0.9, 28, 2.0);
        ball.add(ballLight);

        // -----------------------------
        // UI refs
        // -----------------------------
        const elMessage = document.getElementById("message");
        const elReticle = document.getElementById("reticle");

        const elLevel = document.getElementById("level");
        const elStreak = document.getElementById("streak");
        const elStreakNeed = document.getElementById("streakNeed");
        const elStreakNote = document.getElementById("streakNote");
        const elCoins = document.getElementById("coins");
        const elLives = document.getElementById("lives");

        const elClock = document.getElementById("clock");
        const elSpeed = document.getElementById("speed");
        const elMaxSpeed = document.getElementById("maxSpeed");

        const elOverlay = document.getElementById("overlay");
        const elOverlayTitle = document.getElementById("overlayTitle");
        const elReadyLine = document.getElementById("readyLine");
        const elRuleBlock = document.getElementById("ruleBlock");
        const elOverlayHint = document.getElementById("overlayHint");

        function showMessage(text, ms = 900) {
            elMessage.textContent = text;
            elMessage.style.display = "block";
            if (ms > 0) setTimeout(() => (elMessage.style.display = "none"), ms);
        }
        function showStickyMessage(text) {
            elMessage.textContent = text;
            elMessage.style.display = "block";
        }
        function hideMessage() { elMessage.style.display = "none"; }

        function clearReticle() {
            elReticle.style.display = "none";
            elReticle.classList.remove("good", "pop", "ready");
        }

        function setReticleScreenFromWorld(worldPos) {
            const v = worldPos.clone().project(camera);
            if (v.z < -1 || v.z > 1) { elReticle.style.display = "none"; return; }

            const w = renderer.domElement.clientWidth;
            const h = renderer.domElement.clientHeight;

            const x = (v.x * 0.5 + 0.5) * w;
            const y = (-v.y * 0.5 + 0.5) * h;

            const pad = 18;
            elReticle.style.left = `${THREE.MathUtils.clamp(x, pad, w - pad)}px`;
            elReticle.style.top = `${THREE.MathUtils.clamp(y, pad, h - pad)}px`;
            elReticle.style.display = "block";
        }

        // -----------------------------
        // Post-processing
        // -----------------------------
        const composer = new EffectComposer(renderer);
        composer.addPass(new RenderPass(scene, camera));
        const bloomPass = new UnrealBloomPass(new THREE.Vector2(window.innerWidth, window.innerHeight), 0.55, 0.35, 0.85);
        composer.addPass(bloomPass);

        // -----------------------------
        // RUN STATS (for leaderboards later)
        // -----------------------------
        const RUN_STATS = {
            startedAtISO: null,
            totalPlaySeconds: 0,
            levelReached: 1,
            levelsCompleted: 0,
            coins: 0,
            maxBallSpeed: 0,
            deaths: 0,
            lastBallSpeed: 0
        };
        window.PONG_RUN_STATS = RUN_STATS;

        function formatMMSS(sec) {
            const s = Math.max(0, Math.floor(sec));
            const mm = String(Math.floor(s / 60)).padStart(2, "0");
            const ss = String(s % 60).padStart(2, "0");
            return `${mm}:${ss}`;
        }

        // -----------------------------
        // Game state
        // -----------------------------
        const game = { started: false, paused: true, over: false, mode: "intro" }; // intro | play | pause | win | gameover

        const progression = {
            level: 1,
            streak: 0,
            streakNeed: STREAK_TO_SPAWN_COIN,
            lives: LIVES_PER_LEVEL,
            coinSpawned: false,
            completed: false
        };

        elLevel.textContent = String(progression.level);
        elStreak.textContent = String(progression.streak);
        elStreakNeed.textContent = String(progression.streakNeed);
        elCoins.textContent = String(RUN_STATS.coins);
        elLives.textContent = String(progression.lives);

        function updateStreakUI() {
            elStreak.textContent = String(progression.streak);
            if (progression.coinSpawned) {
                elStreakNeed.textContent = String(progression.streakNeed);
                elStreakNote.textContent = "(coin spawned — hit it!)";
            } else {
                elStreakNeed.textContent = String(progression.streakNeed);
                elStreakNote.textContent = "(to spawn coin)";
            }
        }

        function cpuDifficultyMultiplier() {
            const streakFrac = progression.coinSpawned ? 1 : THREE.MathUtils.clamp(progression.streak / progression.streakNeed, 0, 1);
            const effectiveLevel = (Math.min(progression.level, MAX_LEVEL) - 1) + 0.25 * streakFrac;
            const raw = 1.0 + effectiveLevel * LEVEL_SPEED_STEP;
            return THREE.MathUtils.clamp(raw, CPU_MULT_MIN, CPU_MULT_MAX);
        }

        // -----------------------------
        // Overlay helpers
        // -----------------------------
        function showIntroOverlay() {
            elOverlay.style.display = "flex";
            elOverlayTitle.textContent = "Welcome player...";
            elReadyLine.textContent = "Ready to play?";
            elRuleBlock.style.display = "none";
            elOverlayHint.style.display = "none";
            elReadyLine.classList.remove("enterPulse");

            let pops = 0;
            const doTextPop = () => {
                elReadyLine.classList.remove("text-pop");
                void elReadyLine.offsetWidth;
                elReadyLine.classList.add("text-pop");
            };

            const interval = setInterval(() => {
                pops += 1;
                doTextPop();
                if (pops >= 3) {
                    clearInterval(interval);
                    setTimeout(() => {
                        elRuleBlock.style.display = "block";
                        elOverlayHint.style.display = "flex";
                        elReadyLine.classList.add("enterPulse");
                    }, 260);
                }
            }, 520);
        }

        function showPauseOverlay(title = "Paused", subtitle = "Take a breath.") {
            elOverlay.style.display = "flex";
            elOverlayTitle.textContent = title;
            elReadyLine.textContent = subtitle;
            elRuleBlock.style.display = "none";
            elOverlayHint.style.display = "flex";
            elOverlayHint.innerHTML = `
        <div><span class="kbd enterPulse">Enter</span> to resume</div>
        <div style="opacity:.8">Move: <span class="kbd">A</span>/<span class="kbd">D</span> — Space: serve/hit</div>
      `;
        }

        function showLevelCompleteOverlay() {
            elOverlay.style.display = "flex";
            elOverlayTitle.textContent = `🎉 Level ${progression.level} Complete!`;
            elReadyLine.textContent = "✨ Coin captured!";
            elRuleBlock.style.display = "none";
            elOverlayHint.style.display = "flex";
            elOverlayHint.innerHTML = `
        <div><span class="kbd enterPulse">Enter</span> to continue</div>
        <div style="opacity:.8">Next level starts with 3 ❤️</div>
      `;
        }

        function showGameOverOverlay() {
            elOverlay.style.display = "flex";
            elOverlayTitle.textContent = "Game Over";
            elReadyLine.textContent = "Press Enter to restart";
            elRuleBlock.style.display = "none";
            elOverlayHint.style.display = "flex";
            elOverlayHint.innerHTML = `
        <div><span class="kbd enterPulse">Enter</span> to restart</div>
        <div style="opacity:.8">Coins: 🪙 ${RUN_STATS.coins} — Max speed: ${RUN_STATS.maxBallSpeed.toFixed(1)}</div>
      `;

            updateData(RUN_STATS);  //send to backend
        }

        function hideOverlay() { elOverlay.style.display = "none"; }

        // -----------------------------
        // Serve state + ball state
        // -----------------------------
        const serve = {
            side: "cpu",
            active: true,
            stage: "hold",
            t: 0,
            serveX: 0,
            tossVY: 4.6,
            tossG: 10.5,
            strikeY: 0.60,
            windowDur: 0.28,
            windowT: 0,
            cpuHoldDelay: 0.85,
            cpuStrikeAt: 0.14,
            cpuFaultChance: 0.06
        };

        const ballState = {
            phase: "serve_hold", // serve_hold | approach | return | falling | dead
            missSide: "player",
            pos: new THREE.Vector3(0, 0.6, lane.zServe),
            vel: new THREE.Vector3(),
            approachSpeed: 22,
            returnSpeed: 28,
            gravity: 28,
            playY: 0.15,
            hitZ: 8.8,
            missDropZ: 9.2,
            spinX: 0,
            spinDecay: 0.92,
            magnus: 0.9,
            pop: {
                stage: 0,
                tPop1: null, tPop2: null, tPop3: null,
                hitLead: 0.35,
                armed: false,
                lastTimeToHit: null,
                prevTimeToHit: null,
                scheduled: false,

                blinkDur: 0.18,
                blinkStage: 0,
                blinkStartTTH: null,

                queued: false,
                queuedStage: 0,
                queuedFactor: 1.0
            }
        };
        ball.position.copy(ballState.pos);

        const paddleMotion = { prevX: paddle.position.x, velX: 0 };
        const opponentMotion = { prevX: opponent.position.x, velX: 0 };
        let rallyCount = 0;

        // -----------------------------
        // Coin system (spawn after streak, stays, relocates every minute)
        // -----------------------------
        let coinCount = 0;
        let coin = null;
        let coinActive = false;
        let coinRelocateTimer = 0;
        const coinRadius = 0.9;

        function makeCoinMesh() {
            const g = new THREE.Group();

            const goldMat = new THREE.MeshStandardMaterial({
                color: 0xffd24a,
                roughness: 0.25,
                metalness: 0.95,
                emissive: 0xffb300,
                emissiveIntensity: 0.25
            });

            const body = new THREE.Mesh(new THREE.CylinderGeometry(0.62, 0.62, 0.12, 28, 1, false), goldMat);
            body.rotation.z = Math.PI / 2;
            body.castShadow = true;
            body.receiveShadow = true;
            g.add(body);

            const rim = new THREE.Mesh(new THREE.TorusGeometry(0.62, 0.10, 10, 30), goldMat);
            rim.rotation.y = Math.PI / 2;
            rim.castShadow = true;
            g.add(rim);

            const stampMat = goldMat.clone();
            stampMat.emissiveIntensity = 0.18;
            const stamp = new THREE.Mesh(new THREE.TorusGeometry(0.28, 0.06, 10, 30), stampMat);
            stamp.rotation.y = Math.PI / 2;
            stamp.castShadow = true;
            g.add(stamp);

            return g;
        }

        function despawnCoin() {
            if (coin && coin.parent) coin.parent.remove(coin);
            coin = null;
            coinActive = false;
            coinRelocateTimer = 0;
        }

        function randomCoinPosition() {
            const x = THREE.MathUtils.randFloat(lane.xMin + COIN_X_PAD, lane.xMax - COIN_X_PAD);

            // Random near/far depth: sometimes closer, sometimes far
            // (use slight bias to be mid-range but still random)
            const r = Math.random();
            let z;
            if (r < 0.33) z = THREE.MathUtils.randFloat(-28, COIN_Z_MAX);       // closer cluster
            else if (r < 0.66) z = THREE.MathUtils.randFloat(-62, -28);         // mid cluster
            else z = THREE.MathUtils.randFloat(COIN_Z_MIN, -62);                // far cluster

            const y = ballState.playY + 0.35;
            return { x, y, z };
        }

        function relocateCoin() {
            if (!coin || !coinActive) return;
            const p = randomCoinPosition();
            coin.position.set(p.x, p.y, p.z);
            coin.rotation.y = Math.random() * Math.PI;
            coinRelocateTimer = 0;
            showMessage("🪙 Coin moved!", 550);
        }

        function spawnCoinForLevel() {
            if (coinActive) return;

            despawnCoin();
            coin = makeCoinMesh();
            coinActive = true;
            coinRelocateTimer = 0;

            const p = randomCoinPosition();
            coin.position.set(p.x, p.y, p.z);
            coin.rotation.y = Math.random() * Math.PI;

            scene.add(coin);
        }

        function collectCoin() {
            if (!coinActive) return;
            coinActive = false;

            coinCount += 1;
            RUN_STATS.coins = coinCount;
            elCoins.textContent = String(coinCount);

            despawnCoin();

            onLevelComplete();
        }

        function updateCoin(dt, t) {
            if (!coin || !coinActive) return;

            coinRelocateTimer += dt;
            if (coinRelocateTimer >= COIN_RELOCATE_SECONDS) {
                relocateCoin();
            }

            coin.rotation.x = Math.sin(t * 2.2) * 0.08;
            coin.rotation.y += dt * 2.8;
            coin.position.y = ballState.playY + 0.35 + Math.sin(t * 3.2) * 0.10;

            const dx = ballState.pos.x - coin.position.x;
            const dy = ballState.pos.y - coin.position.y;
            const dz = ballState.pos.z - coin.position.z;
            const rr = (ballRadius + coinRadius) * (ballRadius + coinRadius);

            if ((dx * dx + dy * dy + dz * dz) <= rr) collectCoin();
        }

        // -----------------------------
        // Level flow
        // -----------------------------
        function startLevel(levelNum) {
            progression.level = THREE.MathUtils.clamp(levelNum, 1, MAX_LEVEL);
            progression.streak = 0;
            progression.lives = LIVES_PER_LEVEL;
            progression.coinSpawned = false;
            progression.completed = false;

            elLevel.textContent = String(progression.level);
            elLives.textContent = String(progression.lives);
            updateStreakUI();

            // coin resets per level (but coin count stays global)
            despawnCoin();

            rallyCount = 0;
            prepareServe("cpu");
            showMessage(`Level ${progression.level}`, 700);

            RUN_STATS.levelReached = Math.max(RUN_STATS.levelReached, progression.level);

            game.mode = "play";
        }

        function onLevelComplete() {
            if (progression.completed) return;
            progression.completed = true;

            RUN_STATS.levelsCompleted += 1;

            clearReticle();
            hideMessage();

            game.paused = true;
            game.mode = "win";
            showLevelCompleteOverlay();
        }

        function nextLevel() {
            if (progression.level < MAX_LEVEL) {
                game.paused = false;
                hideOverlay();
                startLevel(progression.level + 1);
            } else {
                game.paused = false;
                hideOverlay();
                showMessage("🏁 You cleared Level 10! Looping Level 10…", 1200);
                startLevel(10);
            }
        }

        function resetStreakOnDrop() {
            if (progression.coinSpawned) return; // coin stays, streak doesn’t matter anymore
            progression.streak = 0;
            updateStreakUI();
        }

        function registerSuccessfulPlayerReturn() {
            if (!progression.coinSpawned) {
                progression.streak = Math.min(progression.streak + 1, progression.streakNeed);
                updateStreakUI();

                if (progression.streak >= progression.streakNeed) {
                    progression.coinSpawned = true;
                    updateStreakUI();
                    spawnCoinForLevel();
                    showMessage("🪙 Coin appeared! Hit it to finish the level.", 1300);
                }
            }
        }

        function loseLifeAndContinue() {
            progression.lives -= 1;
            elLives.textContent = String(progression.lives);

            resetStreakOnDrop();

            if (progression.lives <= 0) {
                ballState.phase = "dead";
                game.over = true;
                game.paused = true;
                game.mode = "gameover";
                RUN_STATS.deaths += 1;
                clearReticle();
                showGameOverOverlay();
                return;
            }

            rallyCount = 0;
            prepareServe("cpu");
            showMessage(`Life lost! ${progression.lives} ❤️ left`, 900);
        }

        // -----------------------------
        // Serve functions
        // -----------------------------
        function prepareServe(serverSide) {
            serve.side = serverSide;
            serve.active = true;
            serve.stage = "hold";
            serve.t = 0;
            serve.windowT = 0;

            ballState.phase = "serve_hold";
            ballState.vel.set(0, 0, 0);

            ballState.pop.armed = false;
            ballState.pop.scheduled = false;
            ballState.pop.stage = 0;
            ballState.pop.prevTimeToHit = null;
            ballState.pop.lastTimeToHit = null;
            ballState.pop.blinkStage = 0;
            ballState.pop.blinkStartTTH = null;
            ballState.pop.queued = false;

            clearReticle();

            if (serverSide === "cpu") {
                ballState.pos.set((Math.random() - 0.5) * 9, ballState.playY, lane.zServe);
                serve.serveX = ballState.pos.x;
                ball.position.copy(ballState.pos);
                showMessage("CPU serving...", 650);
            } else {
                ballState.pos.set(paddle.position.x, ballState.playY, ballState.hitZ);
                serve.serveX = ballState.pos.x;
                ball.position.copy(ballState.pos);
                showStickyMessage("YOUR SERVE: Space to toss");
            }
        }

        function beginToss(serverSide) {
            serve.stage = "toss";
            serve.t = 0;
            serve.windowT = 0;

            serve.serveX = (serverSide === "player") ? paddle.position.x : ballState.pos.x;
            ballState.pos.x = serve.serveX;
            ballState.vel.set(0, serve.tossVY, 0);

            if (serverSide === "player") showStickyMessage("TOSS... (wait)");
            else hideMessage();
        }

        function openStrikeWindow(serverSide) {
            serve.stage = "window";
            serve.windowT = 0;

            clearReticle();
            setReticleScreenFromWorld(new THREE.Vector3(paddle.position.x, 1.05, ballState.hitZ));
            elReticle.classList.add("ready");

            if (serverSide === "player") showStickyMessage("STRIKE NOW: Space");

            serve.cpuStrikeAt = THREE.MathUtils.clamp(0.06 + Math.random() * (serve.windowDur * 0.75), 0.06, serve.windowDur * 0.9);
        }

        function faultServe(serverSide) {
            clearReticle();
            hideMessage();
            showMessage(serverSide === "player" ? "Serve fault!" : "CPU serve fault!", 650);
            prepareServe(serverSide);
        }

        function launchServe(serverSide) {
            serve.active = false;
            serve.stage = "done";
            clearReticle();
            hideMessage();

            ballState.pos.y = ballState.playY;
            ballState.spinX = (Math.random() - 0.5) * 4.5;

            const cpuMult = cpuDifficultyMultiplier();

            if (serverSide === "cpu") {
                ballState.phase = "approach";
                const targetX = paddle.position.x + (Math.random() - 0.5) * 5.0;
                const dx = THREE.MathUtils.clamp(targetX - ballState.pos.x, -10, 10);

                const speed = (ballState.approachSpeed + Math.min(rallyCount * 0.15, 8)) * cpuMult;
                ballState.vel.set(THREE.MathUtils.clamp(dx * 0.40, -7, 7), 0, speed);

                startSequence();
                showMessage("Serve!", 320);
            } else {
                ballState.phase = "return";
                const speed = ballState.returnSpeed + Math.min(rallyCount * 0.15, 8);
                const targetX = opponent.position.x + (Math.random() - 0.5) * 3.0;
                const dx = THREE.MathUtils.clamp(targetX - ballState.pos.x, -10, 10);
                ballState.vel.set(THREE.MathUtils.clamp(dx * 0.55, -10, 10), 0, -speed);
                showMessage("Serve!", 320);
            }
        }

        function updateServe(dt) {
            if (game.paused || game.over) return;
            if (!serve.active) return;
            if (ballState.phase !== "serve_hold") return;

            if (serve.side === "player" && serve.stage === "hold") {
                ballState.pos.x = THREE.MathUtils.lerp(ballState.pos.x, paddle.position.x, 1 - Math.pow(0.0005, dt));
                serve.serveX = ballState.pos.x;
                ball.position.copy(ballState.pos);
            }

            if (serve.side === "cpu" && serve.stage === "hold") {
                serve.t += dt;
                if (serve.t >= serve.cpuHoldDelay) beginToss("cpu");
                return;
            }

            if (serve.stage === "toss") {
                ballState.pos.x = serve.serveX;
                ballState.vel.y -= serve.tossG * dt;
                ballState.pos.y += ballState.vel.y * dt;

                if (ballState.vel.y < 0 && ballState.pos.y <= serve.strikeY) {
                    ballState.pos.y = serve.strikeY;
                    ballState.vel.y = 0;
                    openStrikeWindow(serve.side);
                }
                ball.position.copy(ballState.pos);
                return;
            }

            if (serve.stage === "window") {
                serve.windowT += dt;

                ballState.pos.x = serve.serveX;
                ballState.pos.y = serve.strikeY;
                ball.position.copy(ballState.pos);

                if (serve.windowT > serve.windowDur) { faultServe(serve.side); return; }

                if (serve.side === "cpu" && serve.windowT >= serve.cpuStrikeAt) {
                    if (Math.random() < serve.cpuFaultChance) faultServe("cpu");
                    else launchServe("cpu");
                }
            }
        }

        function attemptPlayerServeStrike() {
            if (!serve.active || serve.side !== "player") return;
            if (serve.stage !== "window") return;
            launchServe("player");
        }

        // -----------------------------
        // Reticle timing helpers
        // -----------------------------
        function stageBaseFactor(stage) {
            if (stage === 1) return 0.82;
            if (stage === 2) return 1.00;
            return 1.18;
        }

        function computeTimingFactor(stage, startTTH, currTTH) {
            const base = stageBaseFactor(stage);
            if (!isFinite(startTTH) || !isFinite(currTTH)) return base;

            const denom = Math.max(1e-6, ballState.pop.blinkDur);
            const progress01 = THREE.MathUtils.clamp((startTTH - currTTH) / denom, 0, 1);
            const extra = 0.10 * progress01;
            return THREE.MathUtils.clamp(base + extra, 0.75, 1.30);
        }

        function isInBlinkWindow(stage, currTTH) {
            if (ballState.pop.blinkStage !== stage) return false;
            const start = ballState.pop.blinkStartTTH;
            if (!isFinite(start) || !isFinite(currTTH)) return false;
            return (currTTH <= start) && (currTTH >= (start - ballState.pop.blinkDur));
        }

        function doPop(stageJustOpened) {
            elReticle.classList.remove("pop");
            void elReticle.offsetWidth;
            elReticle.classList.add("pop");

            ballState.pop.blinkStage = stageJustOpened;
            ballState.pop.blinkStartTTH = ballState.pop.lastTimeToHit;
        }

        function computeTimeToHit() {
            if (ballState.phase !== "approach" || ballState.vel.z <= 0) return null;
            const vz = Math.max(1e-6, ballState.vel.z);
            return (ballState.hitZ - ballState.pos.z) / vz;
        }

        function computePredictedX(timeToHit) {
            return ballState.pos.x + ballState.vel.x * timeToHit;
        }

        function isAlignedEasy(timeToHit) {
            const bladeHalfW = 2.2;
            const slack = 2.0;
            const targetX = computePredictedX(timeToHit);
            const dx = targetX - paddle.position.x;
            const dxNow = ballState.pos.x - paddle.position.x;
            return (Math.abs(dx) <= (bladeHalfW + slack)) || (Math.abs(dxNow) <= (bladeHalfW + slack * 0.85));
        }

        function startSequence() {
            elReticle.style.display = "block";
            elReticle.classList.remove("good", "pop", "ready");

            ballState.pop.stage = 0;
            ballState.pop.tPop1 = null;
            ballState.pop.tPop2 = null;
            ballState.pop.tPop3 = null;
            ballState.pop.lastTimeToHit = null;
            ballState.pop.prevTimeToHit = null;
            ballState.pop.scheduled = false;
            ballState.pop.armed = true;

            ballState.pop.blinkStage = 0;
            ballState.pop.blinkStartTTH = null;

            ballState.pop.queued = false;
            ballState.pop.queuedStage = 0;
            ballState.pop.queuedFactor = 1.0;
        }

        function schedulePops(timeToHit) {
            const interval = THREE.MathUtils.clamp(timeToHit / 3, 0.30, 0.70);
            const hitLead = THREE.MathUtils.clamp(interval * 0.80, 0.28, 0.42);
            ballState.pop.hitLead = hitLead;
            ballState.pop.tPop1 = 2 * interval;
            ballState.pop.tPop2 = 1 * interval;
            ballState.pop.tPop3 = hitLead;
            ballState.pop.scheduled = true;
        }

        // -----------------------------
        // Player hit mechanics
        // -----------------------------
        function performPlayerHit(timingFactor, label = "Hit!") {
            registerSuccessfulPlayerReturn();

            rallyCount += 1;
            clearReticle();

            ballState.pop.armed = false;
            ballState.pop.queued = false;
            ballState.pop.queuedStage = 0;

            ballState.phase = "return";

            const dxNow = ball.position.x - paddle.position.x;
            const offX = THREE.MathUtils.clamp(dxNow, -3.0, 3.0);

            const baseSpeed = (ballState.returnSpeed + Math.min(rallyCount * 0.8, 14));
            const baseZ = -(baseSpeed * timingFactor);

            const intentX = offX * 4.8 + paddleMotion.velX * 0.45 + (Math.random() - 0.5) * 1.2;
            const newVx = THREE.MathUtils.clamp(intentX, -12, 12);

            ballState.spinX = THREE.MathUtils.clamp(
                ballState.spinX * 0.3 + offX * 2.4 + paddleMotion.velX * 0.7,
                -12, 12
            );

            ballState.pos.y = ballState.playY;
            ballState.vel.set(newVx, 0, baseZ);

            showMessage(label, 240);
        }

        function handleHitAttempt() {
            if (serve.active) return;
            if (!ballState.pop.armed) return;
            if (ballState.phase !== "approach") return;

            const currTTH = ballState.pop.lastTimeToHit;
            if (!isFinite(currTTH)) return;

            const stage = ballState.pop.blinkStage;
            if (!(stage === 1 || stage === 2 || stage === 3)) return;
            if (!isInBlinkWindow(stage, currTTH)) return;
            if (!isAlignedEasy(currTTH)) return;

            const factor = computeTimingFactor(stage, ballState.pop.blinkStartTTH, currTTH);

            if (stage === 3) {
                performPlayerHit(factor, "Power!");
            } else {
                ballState.pop.queued = true;
                ballState.pop.queuedStage = stage;
                ballState.pop.queuedFactor = factor;
                showMessage(stage === 1 ? "Wind-up (soft)!" : "Wind-up (clean)!", 260);
            }
        }

        // -----------------------------
        // CPU movement + CPU hit
        // -----------------------------
        function updateOpponent(dt, t) {
            if (game.paused || ballState.phase === "dead") return;

            const cpuMult = cpuDifficultyMultiplier();

            if (ballState.phase !== "return" || ballState.vel.z >= 0) {
                const bob = Math.sin(t * 5.2 + 1.3) * 0.04;
                opponent.position.y = paddleBaseY + bob;

                opponentMotion.velX = (opponent.position.x - opponentMotion.prevX) / Math.max(1e-6, dt);
                opponentMotion.prevX = opponent.position.x;
                return;
            }

            const vz = Math.min(-1e-6, ballState.vel.z);
            const timeToOpp = (opponentZ - ballState.pos.z) / vz;
            const predictedX = ballState.pos.x + ballState.vel.x * timeToOpp;

            const baseErr = 1.8;
            const reactionError = THREE.MathUtils.clamp(baseErr / cpuMult, 0.45, 1.8);

            const targetX = THREE.MathUtils.clamp(
                predictedX + (Math.random() - 0.5) * reactionError * 0.18,
                lane.xMin,
                lane.xMax
            );

            const baseCpuSpeed = 10;
            const cpuSpeed = THREE.MathUtils.clamp(baseCpuSpeed * cpuMult, 10, 18);

            const dx = targetX - opponent.position.x;
            opponent.position.x += THREE.MathUtils.clamp(dx, -cpuSpeed * dt, cpuSpeed * dt);

            const bob = Math.sin(t * 6.0 + 0.7) * 0.05;
            opponent.position.y = paddleBaseY + bob;

            opponentMotion.velX = (opponent.position.x - opponentMotion.prevX) / Math.max(1e-6, dt);
            opponentMotion.prevX = opponent.position.x;
        }

        function beginFalling(reason, missSide) {
            clearReticle();
            hideMessage();
            serve.active = false;
            serve.stage = "done";

            ballState.pop.armed = false;
            ballState.pop.queued = false;

            ballState.phase = "falling";
            ballState.missSide = missSide;

            ballState.pos.y = ballState.playY;
            ballState.pos.z = (missSide === "player") ? ballState.missDropZ : opponentZ;
            ballState.vel.set(ballState.vel.x * 0.25, -2.0, 0);

            showMessage(reason, 650);
        }

        function attemptCpuHit() {
            const cpuMult = cpuDifficultyMultiplier();

            const bladeHalfW = 2.2;
            const cpuSlackBase = 1.25;
            const cpuSlack = THREE.MathUtils.clamp(cpuSlackBase / cpuMult, 0.55, 1.55);

            const aligned = Math.abs(ballState.pos.x - opponent.position.x) <= (bladeHalfW + cpuSlack);

            const missBase = 0.10;
            const missChance = THREE.MathUtils.clamp(missBase / cpuMult, 0.03, 0.22);

            if (!aligned || Math.random() < missChance) {
                beginFalling("CPU missed!", "cpu");
                return;
            }

            rallyCount += 1;

            ballState.phase = "approach";
            ballState.pos.z = opponentZ;
            ballState.pos.y = ballState.playY;

            const offX = THREE.MathUtils.clamp(ballState.pos.x - opponent.position.x, -3.0, 3.0);

            const cpuStage = (Math.random() < 0.20) ? 1 : (Math.random() < 0.75) ? 2 : 3;
            const cpuProgress = Math.random();
            const cpuFactor = THREE.MathUtils.clamp(stageBaseFactor(cpuStage) + 0.10 * cpuProgress, 0.75, 1.30);

            const base = ballState.approachSpeed + Math.min(rallyCount * 0.7, 12);
            const speed = base * cpuFactor * cpuMult;

            const newVx = THREE.MathUtils.clamp(
                offX * 4.6 + opponentMotion.velX * 0.35 + (Math.random() - 0.5) * 1.1,
                -12, 12
            );

            ballState.spinX = THREE.MathUtils.clamp(offX * 2.3 + opponentMotion.velX * 0.55, -12, 12);
            ballState.vel.set(newVx, 0, speed);

            startSequence();
            showMessage(cpuStage === 1 ? "CPU soft!" : cpuStage === 2 ? "CPU hit!" : "CPU power!", 200);
        }

        // -----------------------------
        // Physics helpers
        // -----------------------------
        function bounceSideWalls() {
            if (ballState.pos.x < lane.xMin) {
                ballState.pos.x = lane.xMin + (lane.xMin - ballState.pos.x);
                ballState.vel.x *= -1;
                ballState.spinX *= -0.6;
            } else if (ballState.pos.x > lane.xMax) {
                ballState.pos.x = lane.xMax - (ballState.pos.x - lane.xMax);
                ballState.vel.x *= -1;
                ballState.spinX *= -0.6;
            }
        }

        function applySpinCurve(dt) {
            const curve = ballState.spinX * ballState.magnus * dt * 0.06;
            ballState.vel.x += curve;
            ballState.spinX *= Math.pow(ballState.spinDecay, dt);
        }

        // -----------------------------
        // Updates
        // -----------------------------
        const keys = new Set();

        function updatePaddle(dt, t) {
            if (game.paused) return;

            const speed = 18;
            let dirMove = 0;
            if (keys.has("ArrowLeft") || keys.has("KeyA")) dirMove -= 1;
            if (keys.has("ArrowRight") || keys.has("KeyD")) dirMove += 1;

            paddle.position.x += dirMove * speed * dt;
            paddle.position.x = THREE.MathUtils.clamp(paddle.position.x, lane.xMin, lane.xMax);

            paddleMotion.velX = (paddle.position.x - paddleMotion.prevX) / Math.max(1e-6, dt);
            paddleMotion.prevX = paddle.position.x;

            const bob = Math.sin(t * 6.0) * 0.05;
            paddle.position.y = paddleBaseY + bob;

            paddle.rotation.z = THREE.MathUtils.lerp(paddle.rotation.z, -dirMove * 0.10, 1 - Math.pow(0.001, dt));
            paddle.rotation.y = THREE.MathUtils.lerp(paddle.rotation.y, dirMove * 0.06, 1 - Math.pow(0.001, dt));
            paddle.rotation.x = THREE.MathUtils.lerp(paddle.rotation.x, -0.08, 1 - Math.pow(0.001, dt));

            camera.position.x = THREE.MathUtils.lerp(camera.position.x, paddle.position.x * 0.35, 1 - Math.pow(0.001, dt));
            camera.lookAt(paddle.position.x * 0.14, 1.0, 0);
        }

        function updateSequenceAndReticle(dt) {
            if (game.paused) return;
            if (serve.active) return;

            if (!ballState.pop.armed || ballState.phase !== "approach") {
                elReticle.classList.remove("ready");
                return;
            }

            const timeToHit = computeTimeToHit();
            if (!isFinite(timeToHit)) return;

            if (!isFinite(ballState.pop.prevTimeToHit)) ballState.pop.prevTimeToHit = timeToHit;
            const prev = ballState.pop.prevTimeToHit;
            const curr = timeToHit;
            ballState.pop.prevTimeToHit = curr;
            ballState.pop.lastTimeToHit = curr;

            if (!ballState.pop.scheduled && curr > 0.6) schedulePops(curr);

            setReticleScreenFromWorld(new THREE.Vector3(paddle.position.x, 1.05, ballState.hitZ));

            const aligned = isAlignedEasy(curr);
            if (aligned) elReticle.classList.add("good");
            else elReticle.classList.remove("good");

            if (ballState.pop.scheduled) {
                if (ballState.pop.stage === 0 && prev > ballState.pop.tPop1 && curr <= ballState.pop.tPop1) { ballState.pop.stage = 1; doPop(1); }
                if (ballState.pop.stage === 1 && prev > ballState.pop.tPop2 && curr <= ballState.pop.tPop2) { ballState.pop.stage = 2; doPop(2); }
                if (ballState.pop.stage === 2 && prev > ballState.pop.tPop3 && curr <= ballState.pop.tPop3) { ballState.pop.stage = 3; doPop(3); }
            }

            const bs = ballState.pop.blinkStage;
            const inBlink = (bs === 1 || bs === 2 || bs === 3) && isInBlinkWindow(bs, curr);
            if (inBlink) elReticle.classList.add("ready");
            else elReticle.classList.remove("ready");

            if (ballState.pop.queued && ballState.pop.stage === 3) {
                if (curr >= 0 && curr <= ballState.pop.hitLead) {
                    if (isAlignedEasy(curr)) {
                        const label = (ballState.pop.queuedStage === 1) ? "Soft!" : "Clean!";
                        performPlayerHit(ballState.pop.queuedFactor, label);
                        return;
                    } else {
                        beginFalling("Missed (queued)!", "player");
                        return;
                    }
                }
            }

            if (ballState.pop.stage === 3 && curr < 0) beginFalling("Missed!", "player");
        }

        function updateBall(dt) {
            if (game.paused) return;
            if (ballState.phase === "dead") return;

            if (ballState.phase === "serve_hold") {
                ball.position.copy(ballState.pos);
                return;
            }

            applySpinCurve(dt);

            if (ballState.phase === "approach") {
                ballState.pos.addScaledVector(ballState.vel, dt);
                ballState.pos.y = ballState.playY;
                bounceSideWalls();

                if (ballState.pos.z > ballState.hitZ + 1.3 && ballState.pop.stage < 3) {
                    beginFalling("Missed (late)", "player");
                    return;
                }
            }
            else if (ballState.phase === "return") {
                const prevZ = ballState.pos.z;
                ballState.pos.addScaledVector(ballState.vel, dt);
                ballState.pos.y = ballState.playY;
                bounceSideWalls();

                if (prevZ > opponentZ && ballState.pos.z <= opponentZ) {
                    attemptCpuHit();
                }
            }
            else if (ballState.phase === "falling") {
                ballState.vel.y -= ballState.gravity * dt;
                ballState.pos.addScaledVector(ballState.vel, dt);

                if (ballState.pos.y < -6.2) {
                    if (ballState.missSide === "player") {
                        loseLifeAndContinue();
                    } else {
                        rallyCount = 0;
                        prepareServe("cpu");
                        showMessage("CPU dropped it!", 700);
                    }
                    return;
                }
            }

            ball.position.copy(ballState.pos);
        }

        // -----------------------------
        // Timer + speed UI (and stats)
        // -----------------------------
        function updateTimerAndSpeed(dt) {
            if (game.started && !game.paused && !game.over) {
                RUN_STATS.totalPlaySeconds += dt;
            }

            elClock.textContent = formatMMSS(RUN_STATS.totalPlaySeconds);

            // current ball speed
            let spd = 0;
            if (ballState.phase === "approach" || ballState.phase === "return") {
                spd = ballState.vel.length();
            }
            RUN_STATS.lastBallSpeed = spd;
            if (spd > RUN_STATS.maxBallSpeed) RUN_STATS.maxBallSpeed = spd;

            elSpeed.textContent = spd.toFixed(1);
            elMaxSpeed.textContent = RUN_STATS.maxBallSpeed.toFixed(1);
        }

        // -----------------------------
        // Input
        // -----------------------------
        function onKeyDown(e) {
            if (e.code === "Space" || e.code === "Enter") e.preventDefault();
            keys.add(e.code);

            if (e.code === "Enter") {
                if (game.over) { restartGame(); return; }
                if (!game.started) { startGame(); return; }
                if (game.mode === "win") { nextLevel(); return; }
                if (game.paused) resumeGame();
                else pauseGame();
                return;
            }

            if (game.paused) return;
            if (game.mode !== "play") return;

            if (e.code === "Space") {
                if (serve.active && serve.side === "player") {
                    if (serve.stage === "hold") { beginToss("player"); return; }
                    if (serve.stage === "window") { attemptPlayerServeStrike(); return; }
                    return;
                }
                handleHitAttempt();
            }
        };
        window.addEventListener("keydown", onKeyDown);

        function onKeyUp(e) {
            keys.delete(e.code);
        }
        window.addEventListener("keyup", onKeyUp);

        // -----------------------------
        // Start/pause/restart
        // -----------------------------
        function startGame() {
            game.started = true;
            game.paused = false;
            game.mode = "play";
            hideOverlay();
            showMessage("Go!", 450);

            if (!RUN_STATS.startedAtISO) RUN_STATS.startedAtISO = new Date().toISOString();
        }
        function pauseGame() {
            if (!game.started) return;
            game.paused = true;
            game.mode = "pause";
            clearReticle();
            showPauseOverlay();
        }
        function resumeGame() {
            if (!game.started) return;
            game.paused = false;
            game.mode = "play";
            hideOverlay();
            showMessage("Resume", 420);
        }

        function restartGame() {
            // keep coins for meta economy if you want — but stats reset except coins
            const keepCoins = RUN_STATS.coins;

            RUN_STATS.startedAtISO = null;
            RUN_STATS.totalPlaySeconds = 0;
            RUN_STATS.levelReached = 1;
            RUN_STATS.levelsCompleted = 0;
            RUN_STATS.coins = keepCoins;
            RUN_STATS.maxBallSpeed = 0;
            RUN_STATS.deaths = 0;
            RUN_STATS.lastBallSpeed = 0;

            coinCount = keepCoins;
            elCoins.textContent = String(coinCount);

            game.over = false;
            game.started = false;
            game.paused = true;
            game.mode = "intro";

            startLevel(1);
            showIntroOverlay();
        }

        // -----------------------------
        // Init + loop
        // -----------------------------
        function init() {
            elStreakNeed.textContent = String(STREAK_TO_SPAWN_COIN);
            startLevel(1);
            showIntroOverlay();
        }
        init();

        const clock = new THREE.Clock();

        function animate() {
            const dt = Math.min(clock.getDelta(), 0.033);
            const t = clock.elapsedTime;

            updateServe(dt);
            updatePaddle(dt, t);
            updateOpponent(dt, t);
            updateBall(dt);
            updateSequenceAndReticle(dt);

            updateCoin(dt, t);
            updateTimerAndSpeed(dt);

            composer.render();
            requestAnimationFrame(animate);
        }
        animate();

        function onResize() {
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
            composer.setSize(window.innerWidth, window.innerHeight);
            bloomPass.setSize(window.innerWidth, window.innerHeight);
        };

        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
            window.removeEventListener("resize", onResize);
        }
    }, [canvas]);

    return (
        <div id="pong-game-tjs">
            <div id="hud">
                <div><strong>First-Person Pong</strong></div>

                <div style={{ height: "6px" }}></div>

                <div><strong>Level:</strong> <span id="level">1</span> <span style={{ opacity: 0.8 }}>(1–10)</span></div>
                <div><strong>Streak:</strong> <span id="streak">0</span>/<span id="streakNeed">5</span> <span id="streakNote"
                    style={{ opacity: 0.85 }}>(to spawn coin)</span></div>

                <div style={{ opacity: 0.85, "margin-top": "10px" }}>
                    Move: A/D or ←/→<br />
                    Hit: Space (blink 1/2/3 — earlier = softer, later = faster)<br />
                    Serve: Space to toss, then Space to strike (window)<br />
                    Pause/Resume: Enter
                </div>
            </div>

            <div id="topRight">
                <div class="row"><span class="lab">Time</span><span class="val" id="clock">00:00</span></div>
                <div class="row"><span class="lab">Speed</span><span class="val" id="speed">0.0</span></div>
                <div class="row"><span class="lab">Max</span><span class="val" id="maxSpeed">0.0</span></div>
            </div>

            <div id="statusBar" aria-hidden="true">
                <div class="chunk"><span>🪙</span><span class="num" id="coins">0</span></div>
                <div class="chunk"><span>❤️</span><span class="num" id="lives">3</span></div>
            </div>

            <div id="message"></div>

            <div id="reticle" aria-hidden="true">
                <div class="circle"></div>
                <div class="h"></div>
                <div class="v"></div>
            </div>

            <div id="overlay" role="dialog" aria-modal="true">
                <div class="panel">
                    <div class="title" id="overlayTitle">Welcome player...</div>
                    <div class="line"><span id="readyLine">Ready to play?</span></div>

                    <div class="line rule" id="ruleBlock" style={{ display: "none" }}>
                        <div class="line" style={{ margin: "0 0 6px 0" }}><strong>Rules</strong></div>
                        <div class="line" style={{ margin: 0 }}>• Land <strong>5 returns in a row</strong> (no drop) to <strong>spawn
                            the coin</strong>.</div>
                        <div class="line" style={{ margin: 0 }}>• Then <strong>hit the coin</strong> to complete the level.</div>
                        <div class="line" style={{ margin: 0 }}>• Coin relocates every <strong>60 seconds</strong> if you’re too
                            slow.</div>
                        <div class="line" style={{ margin: 0 }}>• Each level starts with <strong>3 lives</strong>. Miss = lose a
                            life.</div>
                        <div class="line" style={{ margin: 0 }}>• Two-step serve: Space toss, then Space strike (window).</div>
                    </div>

                    <div class="hint" id="overlayHint" style={{ display: "none" }}>
                        <div><span class="kbd enterPulse">Enter</span> to start</div>
                        <div style={{ opacity: 0.8 }}>Press <span class="kbd">Enter</span> anytime to pause/resume</div>
                    </div>
                </div>
            </div>
            <canvas ref={(ref) => setCanvas(ref)}></canvas>
        </div>
    );
}

export default PongGame3D;